<template>
  <span
    class="keyboard-shortcut-hint"
    :class="`keyboard-shortcut-hint--${theme}`"
    :aria-label="ariaLabel"
  >
    <template v-for="(key, index) in keys" :key="`key-${index}`">
      <span v-if="index > 0" class="keyboard-shortcut-hint__plus">+</span>
      <span class="keyboard-shortcut-hint__key">
        {{ key }}
      </span>
    </template>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  /** The keyboard shortcut string (e.g., 'Cmd+K', 'K', 'Ctrl+Shift+P') */
  shortcut: string;
  /** Optional separator for multi-key shortcuts (default: '+') */
  separator?: string;
  /** Optional aria-label override */
  ariaLabel?: string;
  /** Base color theme - 'auto' inherits from parent text color via currentColor (default: 'auto') */
  theme?:
    | 'auto'
    | 'black'
    | 'gray-dark'
    | 'gray-medium-dark'
    | 'gray-medium'
    | 'gray-medium-light'
    | 'gray-light'
    | 'white';
}

const props = withDefaults(defineProps<Props>(), {
  separator: '+',
  theme: 'auto',
});

const keys = computed(() => {
  if (!props.shortcut) return [];

  // Handle special cases for common key names
  const keyMap: Record<string, string> = {
    cmd: '⌘',
    command: '⌘',
    ctrl: 'Ctrl',
    control: 'Ctrl',
    shift: '⇧',
    alt: 'Alt',
    option: '⌥',
    opt: '⌥',
    enter: '⏎',
    return: '⏎',
    escape: 'Esc',
    esc: 'Esc',
    delete: 'Del',
    backspace: '⌫',
    tab: 'Tab',
    space: 'Space',
    up: '↑',
    down: '↓',
    left: '←',
    right: '→',
  };

  let shortcutStr = props.shortcut;

  // Special handling for "Cmd/Ctrl" pattern - simplify to just Ctrl (or ⌘ on Mac)
  // This makes the shortcut display cleaner and more compact
  if (shortcutStr.includes('Cmd/Ctrl') || shortcutStr.includes('cmd/ctrl')) {
    // For now, always show Ctrl for consistency across platforms
    // Could detect Mac and show ⌘ instead if desired
    shortcutStr = shortcutStr.replace(/[Cc]md\/[Cc]trl/g, 'Ctrl');
  }

  // Handle special case where shortcut is just the separator itself (e.g., "+")
  if (shortcutStr === props.separator) {
    return [shortcutStr];
  }

  // Split by the separator and map to display values
  return shortcutStr.split(props.separator).map((key) => {
    const normalizedKey = key.trim().toLowerCase();
    return keyMap[normalizedKey] || key.trim().toUpperCase();
  });
});

const ariaLabel = computed(() => {
  return props.ariaLabel || `Keyboard shortcut: ${props.shortcut}`;
});
</script>

<style lang="scss" scoped>
.keyboard-shortcut-hint {
  @apply tw-inline-flex tw-items-center;
  @apply tw-gap-1; // Small gap between keys for combos

  &__key {
    @apply tw-inline-flex tw-items-center tw-justify-center;
    @apply tw-text-xs tw-font-medium;
    @apply tw-bg-transparent;
    @apply tw-rounded-md; // Each key has its own rounded corners
    padding: 1ch 4px;
    min-width: 10px;
    height: 16px;
    line-height: 1;
  }

  &__plus {
    @apply tw-text-xs;
    font-weight: normal;
  }

  // Auto theme (default) - inherits from parent text color via currentColor
  // Works automatically: white on solid buttons, inherits gray in regular content
  &--auto {
    .keyboard-shortcut-hint__key {
      color: currentColor;
      opacity: 0.8;
      border: 1px solid currentColor;
    }
    .keyboard-shortcut-hint__plus {
      color: currentColor;
      opacity: 0.8;
    }
  }

  // Black theme
  &--black {
    .keyboard-shortcut-hint__key {
      @apply tw-text-black tw-text-opacity-80;
      @apply tw-border tw-border-black tw-border-opacity-80;
    }
    .keyboard-shortcut-hint__plus {
      @apply tw-text-black tw-text-opacity-80;
    }
  }

  // Gray dark theme
  &--gray-dark {
    .keyboard-shortcut-hint__key {
      @apply tw-text-gray-dark tw-text-opacity-80;
      @apply tw-border tw-border-gray-dark tw-border-opacity-80;
    }
    .keyboard-shortcut-hint__plus {
      @apply tw-text-gray-dark tw-text-opacity-80;
    }
  }

  // Gray medium-dark theme
  &--gray-medium-dark {
    .keyboard-shortcut-hint__key {
      @apply tw-text-gray-medium-dark tw-text-opacity-80;
      @apply tw-border tw-border-gray-medium-dark tw-border-opacity-80;
    }
    .keyboard-shortcut-hint__plus {
      @apply tw-text-gray-medium-dark tw-text-opacity-80;
    }
  }

  // Gray medium theme (default)
  &--gray-medium {
    .keyboard-shortcut-hint__key {
      @apply tw-text-gray-medium tw-text-opacity-80;
      @apply tw-border tw-border-gray-medium tw-border-opacity-80;
    }
    .keyboard-shortcut-hint__plus {
      @apply tw-text-gray-medium tw-text-opacity-80;
    }
  }

  // Gray medium-light theme
  &--gray-medium-light {
    .keyboard-shortcut-hint__key {
      @apply tw-text-gray-medium-light tw-text-opacity-80;
      @apply tw-border tw-border-gray-medium-light tw-border-opacity-80;
    }
    .keyboard-shortcut-hint__plus {
      @apply tw-text-gray-medium-light tw-text-opacity-80;
    }
  }

  // Gray light theme
  &--gray-light {
    .keyboard-shortcut-hint__key {
      @apply tw-text-gray-light tw-text-opacity-80;
      @apply tw-border tw-border-gray-light tw-border-opacity-80;
    }
    .keyboard-shortcut-hint__plus {
      @apply tw-text-gray-light tw-text-opacity-80;
    }
  }

  // White theme
  &--white {
    .keyboard-shortcut-hint__key {
      @apply tw-text-white tw-text-opacity-80;
      @apply tw-border tw-border-white tw-border-opacity-80;
    }
    .keyboard-shortcut-hint__plus {
      @apply tw-text-white tw-text-opacity-80;
    }
  }
}
</style>
